'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useMemo, useCallback } from 'react';
import { Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, DropdownSection, Button, Checkbox, Badge, Chip, Divider } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useConnection } from 'wagmi';
import type { ChainType } from '@/constants/chain-specific';
import { ChainId, chainDefaults } from '@/constants/chain-specific';
import type { LiquidityPosition } from '@/hooks/liquidity/useLiquidityPositions';
import { getChainsWithPositions, getMultiChainPositions, getCrossChainStats } from '@/lib/liquidity/cross-chain-positions';
import { formatNumber } from '@/util/balance/formatNumber';
interface CrossChainPositionsDropdownProps {
  /** Current chain ID that's actively connected */
  currentChainId: ChainType;
  /** Callback when positions from other chains should be displayed */
  onSelectCrossChainPositions?: (positions: LiquidityPosition[], selectedChains: ChainType[]) => void;
  /** Optional custom class names */
  className?: string;
}

/**
 * Chain metadata for display
 */
const CHAIN_METADATA: Record<number, {
  name: string;
  logo: string;
  color: string;
}> = {
  [ChainId.SHIBARIUM]: {
    name: 'Shibarium',
    logo: chainDefaults.chainImages[109] || '/images/chains/109.png',
    color: 'warning'
  },
  [ChainId.DOGECHAIN]: {
    name: 'Dogechain',
    logo: chainDefaults.chainImages[2000] || '/images/chains/2000.png',
    color: 'success'
  },
  [ChainId.BASE]: {
    name: 'Base',
    logo: chainDefaults.chainImages[8453] || '/images/chains/8453.png',
    color: 'primary'
  },
  [ChainId.ETH]: {
    name: 'Ethereum',
    logo: chainDefaults.chainImages[1] || '/images/chains/1.png',
    color: 'secondary'
  }
};

/**
 * Get chain display info with fallback
 */
function getChainInfo(chainId: ChainType) {
  return CHAIN_METADATA[chainId] || {
    name: `Chain ${chainId}`,
    logo: '/images/chains/default.png',
    color: 'default' as const
  };
}

/**
 * Cross-chain positions dropdown component
 * Allows users to view their liquidity positions from other chains that have been cached
 */
export function CrossChainPositionsDropdown(t0) {
  const $ = _c(57);
  const {
    currentChainId,
    onSelectCrossChainPositions,
    className
  } = t0;
  const {
    address
  } = useConnection();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [selectedChains, setSelectedChains] = useState(t1);
  const [isOpen, setIsOpen] = useState(false);
  let t2;
  bb0: {
    if (!address) {
      let t3;
      if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = [];
        $[1] = t3;
      } else {
        t3 = $[1];
      }
      t2 = t3;
      break bb0;
    }
    let t3;
    if ($[2] !== address || $[3] !== currentChainId) {
      const chains = getChainsWithPositions(address);
      let t4;
      if ($[5] !== currentChainId) {
        t4 = c => c !== currentChainId;
        $[5] = currentChainId;
        $[6] = t4;
      } else {
        t4 = $[6];
      }
      t3 = chains.filter(t4);
      $[2] = address;
      $[3] = currentChainId;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    t2 = t3;
  }
  const chainsWithPositions = t2;
  let t3;
  bb1: {
    if (!address) {
      t3 = null;
      break bb1;
    }
    let t4;
    if ($[7] !== address) {
      t4 = getCrossChainStats(address);
      $[7] = address;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    t3 = t4;
  }
  const chainStats = t3;
  let t4;
  bb2: {
    if (!chainStats) {
      let t5;
      if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = {
          totalPositions: 0,
          totalValue: 0,
          chains: []
        };
        $[9] = t5;
      } else {
        t5 = $[9];
      }
      t4 = t5;
      break bb2;
    }
    let otherChains;
    let t5;
    let t6;
    if ($[10] !== chainStats.chains || $[11] !== currentChainId) {
      let t7;
      if ($[15] !== currentChainId) {
        t7 = c_0 => c_0.chainId !== currentChainId;
        $[15] = currentChainId;
        $[16] = t7;
      } else {
        t7 = $[16];
      }
      otherChains = chainStats.chains.filter(t7);
      t5 = otherChains.reduce(_temp, 0);
      t6 = otherChains.reduce(_temp2, 0);
      $[10] = chainStats.chains;
      $[11] = currentChainId;
      $[12] = otherChains;
      $[13] = t5;
      $[14] = t6;
    } else {
      otherChains = $[12];
      t5 = $[13];
      t6 = $[14];
    }
    let t7;
    if ($[17] !== otherChains || $[18] !== t5 || $[19] !== t6) {
      t7 = {
        totalPositions: t5,
        totalValue: t6,
        chains: otherChains
      };
      $[17] = otherChains;
      $[18] = t5;
      $[19] = t6;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    t4 = t7;
  }
  const otherChainsStats = t4;
  let t5;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = chainId => {
      setSelectedChains(prev => {
        const next = new Set(prev);
        if (next.has(chainId)) {
          next.delete(chainId);
        } else {
          next.add(chainId);
        }
        return next;
      });
    };
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  const handleChainToggle = t5;
  let t6;
  if ($[22] !== address || $[23] !== onSelectCrossChainPositions || $[24] !== selectedChains) {
    t6 = () => {
      if (!address || selectedChains.size === 0) {
        return;
      }
      const chainIds = Array.from(selectedChains);
      const multiChainData = getMultiChainPositions(address, chainIds);
      const allPositions = multiChainData.flatMap(_temp3);
      onSelectCrossChainPositions?.(allPositions, chainIds);
      setIsOpen(false);
    };
    $[22] = address;
    $[23] = onSelectCrossChainPositions;
    $[24] = selectedChains;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  const handleViewSelected = t6;
  if (!address || chainsWithPositions.length === 0) {
    return null;
  }
  let t7;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      content: "min-w-[280px]"
    };
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Icon icon="mdi:link-variant" className="text-lg" />;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== otherChainsStats.totalPositions) {
    t9 = otherChainsStats.totalPositions > 0 && <Badge content={otherChainsStats.totalPositions} color="primary" size="sm" shape="circle"><span /></Badge>;
    $[28] = otherChainsStats.totalPositions;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== className || $[31] !== t9) {
    t10 = <DropdownTrigger><Button variant="flat" size="sm" className={className} startContent={t8} endContent={t9}>Cross-Chain</Button></DropdownTrigger>;
    $[30] = className;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== otherChainsStats.chains || $[34] !== otherChainsStats.totalPositions || $[35] !== otherChainsStats.totalValue) {
    t11 = <DropdownSection title="Other Chain Positions" showDivider={true}>{otherChainsStats.totalPositions > 0 ? <DropdownItem key="stats" isReadOnly={true} className="cursor-default" textValue="Stats"><div className="flex flex-col gap-1 py-1"><span className="text-xs text-default-500">Found positions on {otherChainsStats.chains.length} other chain(s)</span><div className="flex items-center gap-2 text-sm"><span className="font-medium">{otherChainsStats.totalPositions} positions</span>{otherChainsStats.totalValue > 0 && <><span className="text-default-400">•</span><span className="text-success">${formatNumber(otherChainsStats.totalValue, 2)}</span></>}</div></div></DropdownItem> : <DropdownItem key="no-positions" isReadOnly={true} className="cursor-default text-default-400" textValue="No positions">No positions found on other chains</DropdownItem>}</DropdownSection>;
    $[33] = otherChainsStats.chains;
    $[34] = otherChainsStats.totalPositions;
    $[35] = otherChainsStats.totalValue;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== chainsWithPositions || $[38] !== otherChainsStats.chains || $[39] !== selectedChains) {
    let t13;
    if ($[41] !== otherChainsStats.chains || $[42] !== selectedChains) {
      t13 = chainId_0 => {
        const chainInfo = getChainInfo(chainId_0);
        const stats = otherChainsStats.chains.find(c_3 => c_3.chainId === chainId_0);
        return <DropdownItem key={chainId_0} textValue={chainInfo.name} onPress={() => handleChainToggle(chainId_0)}><div className="flex items-center justify-between w-full"><div className="flex items-center gap-2"><Checkbox isSelected={selectedChains.has(chainId_0)} onValueChange={() => handleChainToggle(chainId_0)} size="sm" /><img src={chainInfo.logo} alt={chainInfo.name} className="w-5 h-5 rounded-full" /><span>{chainInfo.name}</span></div>{stats && <Chip size="sm" variant="flat" color={chainInfo.color as any}>{stats.positionCount}</Chip>}</div></DropdownItem>;
      };
      $[41] = otherChainsStats.chains;
      $[42] = selectedChains;
      $[43] = t13;
    } else {
      t13 = $[43];
    }
    t12 = chainsWithPositions.map(t13);
    $[37] = chainsWithPositions;
    $[38] = otherChainsStats.chains;
    $[39] = selectedChains;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[44] !== t12) {
    t13 = <DropdownSection title="Select Chains to View">{t12}</DropdownSection>;
    $[44] = t12;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  let t14;
  if ($[46] !== handleViewSelected || $[47] !== selectedChains.size) {
    t14 = selectedChains.size > 0 ? <DropdownSection><DropdownItem key="view-selected" color="primary" className="text-primary" onPress={handleViewSelected} textValue="View Selected"><div className="flex items-center gap-2"><Icon icon="mdi:eye" className="text-lg" /><span>View {selectedChains.size} chain(s) positions</span></div></DropdownItem></DropdownSection> : null;
    $[46] = handleViewSelected;
    $[47] = selectedChains.size;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  let t15;
  if ($[49] !== t11 || $[50] !== t13 || $[51] !== t14) {
    t15 = <DropdownMenu aria-label="Cross-chain positions" closeOnSelect={false} selectionMode="none">{t11}{t13}{t14}</DropdownMenu>;
    $[49] = t11;
    $[50] = t13;
    $[51] = t14;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  let t16;
  if ($[53] !== isOpen || $[54] !== t10 || $[55] !== t15) {
    t16 = <Dropdown isOpen={isOpen} onOpenChange={setIsOpen} placement="bottom-end" classNames={t7}>{t10}{t15}</Dropdown>;
    $[53] = isOpen;
    $[54] = t10;
    $[55] = t15;
    $[56] = t16;
  } else {
    t16 = $[56];
  }
  return t16;
}
function _temp3(data) {
  return data.positions;
}
function _temp2(sum_0, c_2) {
  return sum_0 + c_2.totalValue;
}
function _temp(sum, c_1) {
  return sum + c_1.positionCount;
}